import React from 'react';

import type {
	BizyUiBlock,
	CommentDraftCard,
	RecognitionAppreciationCard,
	RecognitionDraftCard,
	RecognitionViewCard,
	UserAdminBatchConfirmCard,
} from '../../../schemas/bizy-ui-blocks';
import { CommentDraftCardView } from './comment-draft-card';
import { RecognitionAppreciationCardView } from './recognition-appreciation-card';
import { RecognitionDraftCardView } from './recognition-draft-card';
import { RecognitionViewCardView } from './recognition-view-card';
import { UserAdminBatchConfirmCardView } from './user-admin-batch-confirm-card';

/**
 * Renderer contract: each Bizy UI block type has a React component
 * that takes the typed block as its only prop and renders a card.
 * The component is responsible for showing payload fields and for
 * wiring `payload.actions` through a {@link UiBlockActionBar}.
 *
 * Adding a new block type:
 *   1. Add the schema entry in `src/schemas/bizy-ui-blocks.ts`.
 *   2. Create the component in this folder.
 *   3. Register it in {@link bizyUiBlockRegistry} below.
 *
 * See the README for the full walkthrough.
 */
export type BizyUiBlockRenderer<TBlock extends BizyUiBlock = BizyUiBlock> = React.ComponentType<{
	block: TBlock;
	fontSize?: 'sm' | 'md';
	isReadOnly?: boolean;
}>;

/**
 * Registry: `block.type` → renderer component.
 *
 * Blocks whose `type` is not listed here fall through to
 * {@link UnknownBlockView} so an older frontend + newer backend
 * degrades gracefully instead of crashing.
 */
export const bizyUiBlockRegistry: Record<string, BizyUiBlockRenderer> = {
	recognition_draft_card: RecognitionDraftCardView as BizyUiBlockRenderer,
	comment_draft_card: CommentDraftCardView as BizyUiBlockRenderer,
	recognition_view_card: RecognitionViewCardView as BizyUiBlockRenderer,
	recognition_appreciation_card: RecognitionAppreciationCardView as BizyUiBlockRenderer,
	user_admin_batch_confirm_card: UserAdminBatchConfirmCardView as BizyUiBlockRenderer,
};

// Strong types for narrowing in tests / consumers that want to check a specific renderer.
export type { CommentDraftCard, RecognitionAppreciationCard, RecognitionDraftCard, RecognitionViewCard, UserAdminBatchConfirmCard };
